import { Col, Row, Spinner } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import TableRowAction from 'components/common/TableRowAction';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useParams } from 'react-router-dom';
import AddOrphanageModal from './AddOrphanageModal';
import AddReportModal from './AddReportModal';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supabase } from 'lib/supabase';
import { toast } from 'react-toastify';
import {
  useBeneficiaryOrphanages,
  useCreateOrphanageOrphan,
  useDeleteOrphanageOrphan,
  useOrphanageReports,
  useCreateOrphanageReport,
  useUpdateOrphanageReport,
  useDeleteOrphanageReport
} from 'hooks/useBeneficiaries';
import useUpload from 'hooks/useUpload';
import { useAuthContext } from 'context/authContext';
import PropTypes from 'prop-types';

// Component to handle document viewing with signed URLs
const ReportDocumentLink = ({ url, name }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      let filePath = '';
      if (url.includes('/storage/v1/object/public/documents/')) {
        filePath = url.split('/storage/v1/object/public/documents/')[1];
      } else if (url.includes('/storage/v1/object/sign/documents/')) {
        filePath = url
          .split('/storage/v1/object/sign/documents/')[1]
          .split('?')[0];
      } else {
        window.open(url, '_blank');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        setLoading(false);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
    setLoading(false);
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{ cursor: loading ? 'wait' : 'pointer' }}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        <FontAwesomeIcon icon="link" />
      )}{' '}
      {name}
    </a>
  );
};

ReportDocumentLink.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};

const Orphanage = () => {
  const { id } = useParams();

  const orphanageColumns = [
    {
      accessor: 'orphanage_name',
      Header: 'Orphanage Name',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { orphanage } = rowData.row.original;
        return <>{orphanage?.name || '-'}</>;
      }
    },
    {
      accessor: 'orphanage_address',
      Header: 'Location',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { orphanage } = rowData.row.original;
        return <>{orphanage?.address || '-'}</>;
      }
    },
    {
      accessor: 'start_date',
      Header: 'Start Date',
      Cell: rowData => {
        const { start_date } = rowData.row.original;
        return start_date ? format(new Date(start_date), 'dd/MM/yyyy') : '-';
      }
    },
    {
      accessor: 'end_date',
      Header: 'End Date',
      Cell: rowData => {
        const { end_date } = rowData.row.original;
        return end_date ? format(new Date(end_date), 'dd/MM/yyyy') : 'Present';
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            isNotEdit
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.orphanage}
          />
        );
      }
    }
  ];

  const reportColumns = [
    {
      accessor: 'reporter',
      Header: 'Reporter',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { user } = rowData.row.original;
        return <>{user ? user.first_name + ' ' + user.last_name : '-'}</>;
      }
    },
    {
      accessor: 'report_date',
      Header: 'Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_date } = rowData.row.original;
        return report_date ? dayjs(report_date).format('DD/MM/YYYY') : '-';
      }
    },
    {
      accessor: 'comment',
      Header: 'Comment',
      cellProps: {
        className: 'py-2',
        style: { maxWidth: '200px' }
      },
      Cell: rowData => {
        const { comment } = rowData.row.original;
        return (
          <span
            style={{
              display: 'block',
              maxWidth: '200px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={comment || ''}
          >
            {comment || '-'}
          </span>
        );
      }
    },
    {
      accessor: 'report_name',
      Header: 'Report File',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_name, report_document } = rowData.row.original;
        return report_document ? (
          <ReportDocumentLink
            url={report_document}
            name={report_name || 'Report'}
          />
        ) : (
          'No File'
        );
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() => handleEditReportClick(rowData.row.original)}
            onCLickDelete={() => handleDeleteReportClick(id)}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        );
      }
    }
  ];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
  const [showEditReportModal, setShowEditReportModal] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [editObj, setEditObj] = useState(null);

  const [addOrphanageModalShow, setAddOrphanageModalShow] = useState(false);
  const [addReportModalShow, setAddReportModalShow] = useState(false);
  const { uploadDocument, loading: isUploadReportLoading } = useUpload();

  const { organization, selectedBranchId, user } = useAuthContext();

  // Use hooks for orphanages
  const {
    beneficiaryOrphanages,
    isLoading: loading,
    refetch: refetchOrphanages
  } = useBeneficiaryOrphanages(id);
  const { createOrphanageOrphan, isCreating: isAddOrphanageLoading } =
    useCreateOrphanageOrphan();
  const { deleteOrphanageOrphan } = useDeleteOrphanageOrphan();

  // Use hooks for orphanage reports
  const {
    orphanageReports,
    isLoading: reportsLoading,
    refetch: refetchOrphanageReports
  } = useOrphanageReports(id);
  const { createOrphanageReport, isCreating: isAddOrphanageReportLoading } =
    useCreateOrphanageReport();
  const { updateOrphanageReport, isUpdating: isUpdateOrphanageReportLoading } =
    useUpdateOrphanageReport();
  const { deleteOrphanageReport } = useDeleteOrphanageReport();

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteReportClick = id => {
    setSelectedId(id);
    setShowDeleteReportModal(true);
  };

  const handleEditReportClick = obj => {
    setEditObj(obj);
    setShowEditReportModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleDeleteReportModalHide = () => {
    setSelectedId(null);
    setShowDeleteReportModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    deleteOrphanageOrphan(selectedId);
    refetchOrphanages();
  };

  const handleConfirmDeleteReport = async () => {
    setShowDeleteReportModal(false);
    deleteOrphanageReport(selectedId);
    refetchOrphanageReports();
  };

  const submitAddOrphanage = async values => {
    createOrphanageOrphan({
      orphanage_id: values.orphanage,
      orphan_id: id,
      branch_id: selectedBranchId,
      organisation_id: organization.id,
      user_id: user.id,
      start_date: values.startDate === '' ? null : values.startDate,
      end_date: values.endDate === '' ? null : values.endDate
    });
    setAddOrphanageModalShow(false);
    refetchOrphanages();
  };

  const submitAddReport = async values => {
    const reportFile = values.reportDocument?.[0];
    let report_name;
    let report_document;

    if (reportFile && reportFile instanceof File) {
      report_name = reportFile.name.split('.')[0];
      try {
        report_document = await uploadDocument(reportFile, 'orphanage-reports');
      } catch (err) {
        return;
      }
    }

    if (editObj) {
      updateOrphanageReport({
        id: editObj.id,
        data: {
          orphanage_id: values.orphanage,
          comment: values.comment,
          reporter: values.reporter,
          user_id: user.id,
          report_date: values.date,
          report_name: report_name || values.editReport?.name || undefined,
          report_document:
            report_document || values.editReport?.url || undefined
        }
      });
      setEditObj(null);
      setShowEditReportModal(false);
    } else {
      createOrphanageReport({
        branch_id: selectedBranchId,
        organisation_id: organization.id,
        orphan_id: id,
        orphanage_id: values.orphanage,
        user_id: user.id,
        reporter: values.reporter,
        comment: values.comment,
        report_date: values.date,
        report_name,
        report_document
      });
      setAddReportModalShow(false);
    }
    refetchOrphanageReports();
  };

  const orphanageOptions = useMemo(() => {
    return (
      beneficiaryOrphanages?.map(item => ({
        label: item.orphanage?.name,
        value: item.orphanage?.id
      })) || []
    );
  }, [beneficiaryOrphanages]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal || showDeleteReportModal}
        onHide={
          showDeleteModal ? handleDeleteModalHide : handleDeleteReportModalHide
        }
        title={showDeleteModal ? 'Remove Orphanage' : 'Delete Report'}
        handleConfirm={
          showDeleteModal ? handleConfirmDelete : handleConfirmDeleteReport
        }
        body={
          showDeleteModal
            ? 'Are you sure you want to remove selected Orphanage?'
            : 'Are you sure you want to remove selected Report?'
        }
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={beneficiaryOrphanages || []}
            columns={orphanageColumns}
            title="Orphanages"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddOrphanageModalShow(true);
            }}
            loading={loading}
            addNew
            subject={permissionsKeyMap.facilities.children.orphanage}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={orphanageReports || []}
            columns={reportColumns}
            title="Reports"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddReportModalShow(true);
              setEditObj(null);
            }}
            loading={reportsLoading}
            addNew
            subject={permissionsKeyMap.facilities.children.orphanage}
          />
        </Col>
      </Row>

      {addOrphanageModalShow && (
        <AddOrphanageModal
          show={addOrphanageModalShow}
          onSubmit={submitAddOrphanage}
          isSubmitLoading={isAddOrphanageLoading}
          onHide={() => {
            setAddOrphanageModalShow(false);
          }}
          title="Add Orphanage"
        />
      )}

      {(addReportModalShow || showEditReportModal) && (
        <AddReportModal
          show={addReportModalShow || showEditReportModal}
          onHide={() => {
            setAddReportModalShow(false);
            setShowEditReportModal(false);
          }}
          title={addReportModalShow ? 'Add Report' : 'Edit Report'}
          loading={
            isAddOrphanageReportLoading ||
            isUpdateOrphanageReportLoading ||
            isUploadReportLoading
          }
          onSubmit={submitAddReport}
          orphanageOptions={orphanageOptions}
          initValues={editObj}
        />
      )}
    </>
  );
};

export default Orphanage;
